import React from 'react';
import { Volume2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { WordStatus } from '../hooks/useShadowingSentenceTracker';
import ShadowingMicPopover from './ShadowingMicPopover';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';

export interface ShadowingSentenceOverlayProps {
  currentSentence: string;
  sentencesCount: number;
  currentSentenceIndex: number;
  wordStatuses: WordStatus[];
  isListening: boolean;
  isTtsPlaying: boolean;
  hasTtsFinished: boolean;
  transcript: string;
  isSentenceComplete: boolean;
  ttsRate: number;
  onCycleTtsRate: () => void;
  onPlayTts: () => void;
  onPlayWordTts: (word: string) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onPrevSentence: () => void;
  onNextSentence: () => void;
  onCloseShadowing: () => void;
  sourceLanguage?: string;
}

/**
 * Modo Shadowing (Design 2.0, minimalista): tela cheia escura e quente, só a frase em destaque,
 * progresso fino no topo e os controles numa única fileira embaixo.
 */
export const ShadowingSentenceOverlay: React.FC<ShadowingSentenceOverlayProps> = ({
  currentSentence,
  sentencesCount,
  currentSentenceIndex,
  wordStatuses,
  isListening,
  isTtsPlaying,
  hasTtsFinished,
  transcript,
  isSentenceComplete,
  ttsRate,
  onCycleTtsRate,
  onPlayTts,
  onPlayWordTts,
  onStartRecording,
  onStopRecording,
  onPrevSentence,
  onNextSentence,
  onCloseShadowing,
  sourceLanguage
}) => {
  const { t, i18n } = useTranslation();
  // O hook precisa vir antes de qualquer return (antes ficava depois e quebrava a ordem dos hooks).
  const unifiedDict = useUnifiedDictionary();

  if (!currentSentence || sentencesCount === 0) return null;

  const handleWordClick = (ws: WordStatus, e?: React.MouseEvent<HTMLElement>) => {
    onPlayWordTts(ws.word);

    const cleanWord = ws.word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!cleanWord) return;

    const nativeLang = (i18n.language || (typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : 'pt') || 'pt').split('-')[0].toLowerCase();
    const srcLang = (sourceLanguage || 'en').split('-')[0].toLowerCase();

    unifiedDict.handleWordClick(e || {} as any, cleanWord, srcLang, nativeLang);
  };

  const progress = sentencesCount > 0 ? ((currentSentenceIndex + 1) / sentencesCount) * 100 : 0;

  return (
    <div
      className="fixed inset-0 z-[200] pointer-events-auto flex flex-col text-[#E6E0D6] bg-[#1C2430]/[0.97] backdrop-blur-2xl animate-in fade-in duration-500"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Topo: rótulo, contador e fechar */}
      <div className="shrink-0 px-5 md:px-8 pt-[max(20px,env(safe-area-inset-top))]">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#E6E0D6]/45">Shadowing</p>
            <p className="mt-1 font-mono text-[12px] tabular-nums text-[#E6E0D6]/70">
              <span key={currentSentenceIndex} className="inline-block animate-in fade-in slide-in-from-bottom-2 duration-300">{currentSentenceIndex + 1}</span>
              <span className="text-[#E6E0D6]/35"> / {sentencesCount}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onCloseShadowing}
            aria-label={t('shadowing.close', 'Fechar Shadowing')}
            className="group w-10 h-10 rounded-full flex items-center justify-center text-[#E6E0D6]/60 hover:text-[#E6E0D6] hover:bg-white/[0.07] transition-colors duration-300"
          >
            <X size={18} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
          </button>
        </div>
        <div className="max-w-3xl mx-auto mt-4 h-[2px] rounded-full bg-white/[0.07] overflow-hidden">
          <div
            className="h-full rounded-full bg-[#E6E0D6]/70 transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Frase em foco */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-5 md:px-8">
        <div key={currentSentenceIndex} className="relative w-full max-w-3xl animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div
            className="max-h-[52vh] overflow-y-auto custom-scrollbar-25 py-4 text-center select-none"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent' }}
          >
            <p className="font-serif text-[26px] md:text-[40px] leading-[1.45] tracking-[-0.01em] flex flex-wrap justify-center gap-x-2 gap-y-1">
              {wordStatuses.length > 0 ? (
                wordStatuses.map((ws) => {
                  let colorClasses = 'text-[#E6E0D6]/85 hover:text-[#E6E0D6] hover:bg-white/[0.06]';
                  if (ws.status === 'correct') {
                    colorClasses = 'text-ok';
                  } else if (ws.status === 'incorrect') {
                    colorClasses = 'text-bad underline decoration-bad/40 decoration-2 underline-offset-[8px]';
                  }

                  return (
                    <span
                      key={ws.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWordClick(ws, e);
                      }}
                      className={`inline-block px-1 rounded-lg cursor-pointer transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-95 ${colorClasses}`}
                      title={`${t('shadowing.listenWord', 'Ouvir')} "${ws.word}"`}
                    >
                      {ws.word}
                    </span>
                  );
                })
              ) : (
                <span>{currentSentence}</span>
              )}
            </p>
          </div>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={onPlayTts}
              className="group inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12px] font-medium text-[#E6E0D6]/60 hover:text-[#E6E0D6] hover:bg-white/[0.07] transition-colors duration-300"
            >
              <Volume2 size={15} strokeWidth={1.75} className={isTtsPlaying ? 'text-ok animate-pulse' : 'transition-transform duration-300 group-hover:scale-110'} />
              {t('shadowing.listenSentence', 'Ouvir frase')}
            </button>
          </div>
        </div>
      </div>

      {/* Controles */}
      <div className="shrink-0 px-5 md:px-8 pb-[max(24px,env(safe-area-inset-bottom))]">
        <ShadowingMicPopover
          isListening={isListening}
          hasTtsFinished={hasTtsFinished}
          isTtsPlaying={isTtsPlaying}
          transcript={transcript}
          wordStatuses={wordStatuses}
          isSentenceComplete={isSentenceComplete}
          ttsRate={ttsRate}
          onCycleTtsRate={onCycleTtsRate}
          onStartRecording={onStartRecording}
          onStopRecording={onStopRecording}
          onReplayTts={onPlayTts}
          onPrevSentence={onPrevSentence}
          onNextSentence={onNextSentence}
        />
      </div>
    </div>
  );
};

export default ShadowingSentenceOverlay;
